import React, { useState, useCallback, useEffect, useRef } from 'react';
import useThrottle from '../../../../globals/hooks/throttle';
import TopLevelCategoryItem from './top-level-category-item';
import TopLevelCategory from './top-level-category';
import { Category } from './types';
import * as S from './styles';

type Props = {
  categories: Category[];
  openCategoryId?: string;
  onOpenCategory: (id: string) => void;
  onClose: () => void;
  onOpenCategoryLink: (id: string) => void;
};

const MegaMenu = ({
  categories,
  openCategoryId,
  onOpenCategory,
  onClose,
  onOpenCategoryLink,
}: Props) => {
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const subMenuArea: React.RefObject<HTMLDivElement> = React.createRef();

  const updateScrolledDownState = useThrottle(
    useCallback(() => {
      if (!(subMenuArea.current instanceof Element)) {
        return;
      }

      if (isScrolledDown && subMenuArea.current.scrollTop === 0) {
        setIsScrolledDown(false);
      } else if (!isScrolledDown && subMenuArea.current.scrollTop > 0) {
        setIsScrolledDown(true);
      }
    }, [isScrolledDown, subMenuArea])
  );

  const onClickCategoryLink = useCallback(
    (id: string) => {
      onOpenCategoryLink(id);
      onClose();
    },
    [onClose, onOpenCategoryLink]
  );

  const firstTopLevelCategoryButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (firstTopLevelCategoryButtonRef.current) {
      firstTopLevelCategoryButtonRef.current.focus();
    }
  }, []);

  return (
    <S.Wrapper data-test="MegaMenu">
      <S.TopLevelMenu>
        {categories.map((category, index) => (
          <TopLevelCategoryItem
            key={category.id}
            id={category.id}
            name={category.name}
            logo={category.logo ? category.logo['96'] : undefined}
            isOpen={openCategoryId === category.id}
            onClickCategoryButton={onOpenCategory}
            buttonRef={index === 0 ? firstTopLevelCategoryButtonRef : undefined}
          />
        ))}
      </S.TopLevelMenu>

      <S.SubMenuArea onScroll={updateScrolledDownState} ref={subMenuArea}>
        {categories.map(category => (
          <TopLevelCategory
            key={category.id}
            category={category}
            isOpen={openCategoryId === category.id}
            isScrolledDown={isScrolledDown}
            onClickCategoryLink={onClickCategoryLink}
          />
        ))}
      </S.SubMenuArea>
    </S.Wrapper>
  );
};

export default MegaMenu;
